<script setup lang="ts">
import { useVModel } from '@vueuse/core';
import {
  HostedFields,
  type hostedFields as hostedFieldsFactory,
  HostedFieldsStateObject,
  HostedFieldsTokenizePayload,
} from 'braintree-web';
// @ts-expect-error (module exists, necessary to improve tree-shaking)
import hostedFields from 'braintree-web/hosted-fields';
import { computed, onMounted, ref, watch, watchEffect } from 'vue';

import FieldErrorIcon from '@/components/base/assets/FieldErrorIcon.vue';
import FloatingLabel from '@/components/base/form/FloatingLabel.vue';
import InlineError from '@/components/base/form/InlineError.vue';
import { borderRules, sizeRules } from '@/composables/useForm';

const props = defineProps<{
  braintreeToken?: string;
  cardType?: string;
  modelValue?: HostedFieldsTokenizePayload;
}>();

const emit = defineEmits(['update:modelValue']);
const tokenizedResult = useVModel(props, 'modelValue', emit);

const cvvDirtyStatus = ref(false);
const cardCvvInput = ref<HTMLElement>();
const cvvConfirmationForm = ref<HostedFields>();
const cvvFieldState = ref<HostedFieldsStateObject['fields']>();

const errorMessages = computed(() => {
  const errors = [];
  if (cvvDirtyStatus.value) {
    const { cvv } = cvvFieldState.value ?? {};
    if (cvv?.isEmpty) errors.push('Please enter the CVV number');
    if (!cvv?.isValid) errors.push('Please enter a valid CVV number');
  }
  return errors;
});

const borderColorForField = () => {
  const { cvv } = cvvFieldState.value ?? {};
  if (cvvDirtyStatus.value) {
    if (cvv?.isFocused ?? false) return 'border-black';
    if (!cvv?.isValid ?? false) return 'border-nuts-red-800';
  }
  return borderRules.color;
};

const borderUtilities = computed(() => [borderRules.width, borderRules.style]);

const sizeUtilities = computed(() => [sizeRules.height, sizeRules.padding, sizeRules.width]);

const cvvHasData = () => {
  const { cvv } = cvvFieldState.value ?? {};
  if (!cvv) return false;
  return !cvv?.isEmpty ?? false;
};

const focusCvv = () => {
  cvvConfirmationForm.value?.focus('cvv');
};

onMounted(() => {
  const stopWatching = watch(
    () => props.braintreeToken,
    async () => {
      if (!props.braintreeToken) return;
      // @ts-expect-error (we don't have to type the number field, as BT docs say it's not necessary)
      cvvConfirmationForm.value = await (hostedFields as typeof hostedFieldsFactory).create({
        authorization: props.braintreeToken,
        // @ts-expect-error (we don't have to type the number field, as BT docs say it's not necessary)
        fields: {
          cvv: {
            container: cardCvvInput.value,
            maxlength: props.cardType === 'American Express' ? 4 : 3,
            minlength: props.cardType === 'American Express' ? 4 : 3,
          },
        },
        styles: {
          input: 'font-sans text-base text-black leading-4 p-0',
        },
      });
      cvvFieldState.value = cvvConfirmationForm.value?.getState()?.fields;

      const trackFieldState = (event: HostedFieldsStateObject) => {
        cvvFieldState.value = event?.fields;
      };

      cvvConfirmationForm.value?.on('blur', (event) => {
        if (event.emittedBy === 'cvv') cvvDirtyStatus.value = true;
        trackFieldState(event);
      });
      cvvConfirmationForm.value?.on('empty', trackFieldState);
      cvvConfirmationForm.value?.on('focus', trackFieldState);
      cvvConfirmationForm.value?.on('notEmpty', trackFieldState);
      cvvConfirmationForm.value?.on('validityChange', trackFieldState);

      stopWatching();
    },
    { immediate: true },
  );
});

watchEffect(async () => {
  if (!cvvFieldState.value?.cvv.isValid) {
    if (tokenizedResult.value) tokenizedResult.value = undefined;
    return;
  }

  try {
    tokenizedResult.value = await cvvConfirmationForm.value?.tokenize();
  } catch (e) {
    console.error('Failed to tokenize card info:', e);
    tokenizedResult.value = undefined;
  }
});
</script>

<template>
  <div>
    <transition name="fade" :duration="2000">
      <div v-show="cvvConfirmationForm">
        <div class="flex items-center mt-6 md:mt-8">
          <div class="relative w-44 md:w-64">
            <div
              class="bg-white"
              :class="[borderUtilities, sizeUtilities, borderColorForField()]"
              ref="cardCvvInput"
              @click="focusCvv"
            />
            <FloatingLabel
              :active="cvvHasData"
              alternateBackground
              for="cvv"
              :invalid="errorMessages.length > 0"
            >
              CVV*
            </FloatingLabel>
            <FieldErrorIcon :showErrorIcon="errorMessages.length > 0" />
            <InlineError :errors="errorMessages" fieldName="Credit Card CVV" />
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>
